import { RestrictedRoute } from 'components/Routes/RestrictedRoute';
import { routePaths } from 'components/Routes/data';
import React from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';

import {
  BulkUploadStep,
  BulkUploadTypes,
  arrayGenericBulkUploadTypes,
  routeToStepMap,
  uploadTypeConfigMap,
} from 'constants/bulkUpload';
import { useSearchParams } from 'hooks/useSearchParams';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import useReduxSelector from 'utils/useReduxSelector';
import BulkUploadsPreview from './BulkUploadsPreview';
import { PageHeader } from '../BulkUploads/BladeComponents/PageHeader';
import { BulkUploadView } from '../BulkUploads/BladeComponents/BulkUploadView';
import useIsFeatureDisabledForRearch from 'access/useIsFeatureDisabledForRearch';
import { WizardWrapper } from 'components/WizardViews/components';
import { WizardInfoProvider } from 'components/WizardViews/providers';
import { WizardStep } from 'forms/Wizard';
import { Wizard } from 'forms/Wizard';
import { Box } from '@razorpay/blade/components';
import { FormProvider } from 'forms';
import { useBulkUploadTypeConfig } from './hooks/useBulkUploadTypeConfig';
import WizardPathToStepController from 'components/WizardViews/components/WizardPathToStepController';

const BulkUploadsInitiate = () => {
  const location = useLocation();
  const { urlParams } = useSearchParams<'type'>();
  const { isAdditionsDisabled, isDeductionsDisabled, isLOPDisabled } =
    useIsFeatureDisabledForRearch();

  const bulkUploadType = parseInt(
    urlParams.get('type') || '-1',
  ) as keyof typeof uploadTypeConfigMap;

  const isOnProPlan = useReduxSelector(loggedInUserSelectors.isOnProPlan);
  const isValidUploadType = arrayGenericBulkUploadTypes.includes(bulkUploadType);
  const organizationId = useReduxSelector(loggedInUserSelectors.organizationID);
  const uploadTypeConfig = useBulkUploadTypeConfig(bulkUploadType);

  if (isAdditionsDisabled || isDeductionsDisabled || isLOPDisabled) {
    if (
      [BulkUploadTypes.TYPE_BULK_FNF_INITIATION, BulkUploadTypes.TYPE_BULK_FNF_SETTLEMENT].includes(
        bulkUploadType,
      )
    ) {
      return <Redirect to={routePaths.unknown} />;
    }
  }

  if (!isOnProPlan || !organizationId || !isValidUploadType) {
    return <Redirect to={routePaths.unknown} />;
  }

  const currentStep =
    routeToStepMap[bulkUploadType][location.pathname] || BulkUploadStep.DOWNLOAD_AND_UPLOAD_STEP;

  return (
    <FormProvider initialState={{}}>
      <WizardInfoProvider beginStep={1} initialStep={currentStep} totalSteps={2}>
        <WizardPathToStepController routeStepMap={routeToStepMap[bulkUploadType]} />
        <WizardWrapper>
          <PageHeader
            currentStep={currentStep}
            headerText={uploadTypeConfig.initiation.pageTitle}
            showProgress={true}
            actionHeaderText={uploadTypeConfig.initiation.actionHeaderText}
          />

          <Switch>
            <Box flex={1} overflowY="auto" height="100%">
              <Wizard>
                <WizardStep
                  step={1}
                  component={() => (
                    <RestrictedRoute
                      allowedPermissions={uploadTypeConfig.permissions ?? []}
                      render={() => <BulkUploadView />}
                      path={[routePaths.bulkUploads.root, routePaths.bulkUploads.initiate]}
                      {...(uploadTypeConfig.initiation.feature
                        ? { allowedFeatures: [uploadTypeConfig.initiation.feature] }
                        : {})}
                      exact
                    />
                  )}
                />
                <WizardStep
                  step={2}
                  component={() => (
                    <RestrictedRoute
                      allowedPermissions={uploadTypeConfig.permissions ?? []}
                      render={() => <BulkUploadsPreview />}
                      path={[routePaths.bulkUploads.preview]}
                      {...(uploadTypeConfig.preview.feature
                        ? { allowedFeatures: [uploadTypeConfig.preview.feature] }
                        : {})}
                      exact
                    />
                  )}
                />
              </Wizard>
            </Box>

            <Route>
              <Redirect to={routePaths.unknown} />
            </Route>
          </Switch>
        </WizardWrapper>
      </WizardInfoProvider>
    </FormProvider>
  );
};

export default BulkUploadsInitiate;
